<script setup lang="ts">
import { computed } from 'vue'
import { AltArrowDownIcon } from '@solar-icons/vue/linear'

import { useDisclosure, type UseDisclosureTriggerAttrs } from '../../../composables/useDisclosure'
import AppBadge from '../AppBadge/AppBadge.vue'
import type { BadgeTone } from '../AppBadge/badge-types'
import { vCollapsible } from '../v-collapsible'
import AppPanel from './AppPanel.vue'
import AppPanelBody from './AppPanelBody.vue'
import AppPanelRim from './AppPanelRim.vue'

const props = withDefaults(
  defineProps<{
    title?: string
    /** A quiet count after the title, such as the rows inside. */
    count?: number
    /** A tinted count after the title, for what still needs the reader. */
    badge?: number
    badgeTone?: BadgeTone
    /** Initial open state when uncontrolled. Ignored once `open` is supplied. */
    defaultOpen?: boolean
    /** Controlled open state; leave `undefined` to let the section own it. */
    open?: boolean
    /** Always open: the rim is a plain caption with no chevron and no button. */
    fixed?: boolean
    /** The body's layout, as on `AppPanelBody`. */
    bodyVariant?: 'default' | 'list' | 'flow' | 'stack' | 'flush' | 'document'
    /** The body's card, as on `AppPanelBody`. */
    surface?: 'glass' | 'paper'
  }>(),
  {
    title: undefined,
    count: undefined,
    badge: undefined,
    badgeTone: 'orange',
    defaultOpen: false,
    open: undefined,
    fixed: false,
    bodyVariant: 'list',
    surface: 'paper',
  },
)

const emit = defineEmits<{
  'update:open': [open: boolean]
}>()

defineSlots<{
  /** The body's content; without it the section is a rim alone. */
  default?: () => unknown
  /** The rim's end, in place of the count or badge. */
  meta?: () => unknown
  /** The whole trigger, for a header of the caller's own; it must be a button wired with `triggerAttrs` and `toggle`. */
  rim?: (scope: {
    open: boolean
    toggle: () => void
    triggerAttrs: UseDisclosureTriggerAttrs
  }) => unknown
}>()

const controlledOpen = computed(() => props.open === true)

const { isOpen, toggle, triggerAttrs, contentAttrs } = useDisclosure({
  defaultOpen: props.defaultOpen,
  open: props.open === undefined ? undefined : controlledOpen,
  onOpenChange: (next) => emit('update:open', next),
})

const expanded = computed(() => props.fixed || isOpen.value)
</script>

<template>
  <AppPanel>
    <slot v-if="$slots.rim && !fixed" name="rim" :open="isOpen" :toggle :trigger-attrs />
    <component
      :is="fixed ? 'div' : 'button'"
      v-else
      :type="fixed ? undefined : 'button'"
      v-bind="fixed ? undefined : triggerAttrs"
      :class="
        fixed
          ? undefined
          : 'w-full cursor-pointer rounded-6 text-left outline-none transition-colors duration-fast ease-soft hovered:bg-bg-wash motion-reduce:transition-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent-blue'
      "
      @click="fixed ? undefined : toggle()"
    >
      <AppPanelRim strong heading-level="span" :title>
        <template #meta>
          <span class="flex items-center gap-4">
            <slot name="meta">
              <AppBadge v-if="badge !== undefined && badge > 0" :tone="badgeTone">{{
                badge
              }}</AppBadge>
              <span v-else-if="count !== undefined" class="tabular-nums">{{ count }}</span>
            </slot>
            <AltArrowDownIcon
              v-if="!fixed"
              aria-hidden="true"
              class="shrink-0 text-icon-tertiary transition-transform duration-slower ease-spring-bouncy motion-reduce:transition-none"
              :class="isOpen ? 'rotate-0' : '-rotate-90'"
            />
          </span>
        </template>
      </AppPanelRim>
    </component>

    <AppPanelBody v-if="$slots.default && fixed" :variant="bodyVariant" :surface>
      <slot />
    </AppPanelBody>
    <div
      v-else-if="$slots.default"
      v-collapsible="expanded"
      class="collapsible"
      :class="{ 'contain-inline-size': !expanded }"
    >
      <div :inert="expanded ? undefined : true">
        <div v-bind="contentAttrs">
          <AppPanelBody :variant="bodyVariant" :surface><slot /></AppPanelBody>
        </div>
      </div>
    </div>
  </AppPanel>
</template>
